import { Table, Transfer, Modal } from 'antd';
import type { GetProp, TableColumnsType, TableProps, TransferProps } from 'antd';
import { useAdminStore } from '@/store/admin';
import { useState, useEffect } from 'react';
import { Button } from '@mantine/core';
import { useTranslation } from 'react-i18next';
import { setUserDepartment, unsetUserDepartment } from '@mygpt/app';
type TransferItem = GetProp<TransferProps, 'dataSource'>[number];
type TableRowSelection<T extends object> = TableProps<T>['rowSelection'];

interface DataType {
  key: string;
  name: string;
}

interface TableTransferProps extends TransferProps<TransferItem> {
  dataSource: DataType[];
  leftColumns: TableColumnsType<DataType>;
  rightColumns: TableColumnsType<DataType>;
}

interface User {
  id: string;
  name: string;
  email: string;
  enabled: boolean;
  departmentIds?: string[];
}

interface DepartmentMember {
  id: string;
}

const filterOption = (input: string, item: DataType) => item.name.includes(input);

const TableTransfer: React.FC<TableTransferProps> = (props) => {
  const { leftColumns, rightColumns, ...restProps } = props;

  return (
    <Transfer style={{ width: '100%' }} showSearch filterOption={filterOption} {...restProps}>
      {({
        direction,
        filteredItems,
        onItemSelect,
        onItemSelectAll,
        selectedKeys: listSelectedKeys,
        disabled: listDisabled,
      }) => {
        const columns = direction === 'left' ? leftColumns : rightColumns;
        const rowSelection: TableRowSelection<TransferItem> = {
          getCheckboxProps: () => ({ disabled: listDisabled }),
          onChange(selectedRowKeys) {
            onItemSelectAll(selectedRowKeys, 'replace');
          },
          selectedRowKeys: listSelectedKeys,
          selections: [Table.SELECTION_ALL, Table.SELECTION_INVERT, Table.SELECTION_NONE],
        };

        return (
          <Table
            rowSelection={rowSelection}
            columns={columns}
            dataSource={filteredItems}
            size="small"
            style={{ pointerEvents: listDisabled ? 'none' : undefined, maxHeight: '400px', overflowY: 'auto' }}
            pagination={false}
            locale={{
              emptyText: restProps.locale?.notFoundContent || 'No Data',
            }}
            onRow={({ key, disabled: itemDisabled }) => ({
              onClick: () => {
                if (itemDisabled || listDisabled) {
                  return;
                }
                onItemSelect(key, !listSelectedKeys.includes(key));
              },
            })}
          />
        );
      }}
    </Transfer>
  );
};

const App: React.FC<{
  opened: boolean;
  onClose: () => void;
  selectedDepartment: { id: string; members: DepartmentMember[] };
  onSave: (selectedMembers: any[]) => void;
}> = ({ opened, onClose, selectedDepartment, onSave }) => {
  const { t } = useTranslation();
  const departmentMembers: DepartmentMember[] = selectedDepartment.members;
  const { usersRes } = useAdminStore((state) => state);
  const users: User[] = usersRes.users || [];
  const [loading, setLoading] = useState(false);

  const [targetKeys, setTargetKeys] = useState<string[]>(() => {
    return departmentMembers.map((member: DepartmentMember) => member.id);
  });

  const onChange: TableTransferProps['onChange'] = (nextTargetKeys: React.Key[]) => {
    setTargetKeys(nextTargetKeys as string[]);
  };

  const handleSave = async () => {
    setLoading(true);
    const selectedMembers = users.filter((user) => targetKeys.includes(user.id));
    const currentMemberIds = departmentMembers.map((member) => member.id);

    // 计算需要添加和移除的用户ID
    const idsToAdd = targetKeys.filter((id) => !currentMemberIds.includes(id));
    const idsToRemove = currentMemberIds.filter((id) => !targetKeys.includes(id));

    try {
      // 调用设置用户部门的API
      if (idsToAdd.length > 0) {
        await setUserDepartment({ userIds: idsToAdd, departmentId: selectedDepartment.id });
      }

      // 调用移除用户部门的API
      if (idsToRemove.length > 0) {
        await unsetUserDepartment({ userIds: idsToRemove, departmentId: selectedDepartment.id });
      }

      onSave(selectedMembers);
      onClose();
    } catch (error) {
      console.error('Failed to update department members:', error);
      // 可以在这里添加错误提示
    } finally {
      setLoading(false);
    }
  };

  const leftDataSource = users
    .filter((user) => !targetKeys.includes(user.id))
    .map((user) => ({
      key: user.id,
      name: user.name,
    }));

  const rightDataSource = users
    .filter((user) => targetKeys.includes(user.id))
    .map((user) => ({
      key: user.id,
      name: user.name,
    }));

  useEffect(() => {
    if (opened) {
      console.log('targetKeys', targetKeys);
    }
  }, [targetKeys, opened]);

  const columns: TableColumnsType<DataType> = [
    {
      dataIndex: 'name',
      title: t('adminConsole.memberTransferModal.name'),
    },
  ];

  return (
    <Modal
      open={opened}
      onCancel={onClose}
      onOk={handleSave}
      closable={false}
      footer={[
        <div key="footer-buttons" style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '1rem' }}>
          <Button variant="outline" onClick={onClose} disabled={loading}>
            {t('adminConsole.memberTransferModal.cancel')}
          </Button>
          <Button type="submit" onClick={handleSave} className="ml-2" loading={loading}>
            {t('adminConsole.memberTransferModal.saveChanges')}
          </Button>
        </div>,
      ]}
    >
      <TableTransfer
        dataSource={[...leftDataSource, ...rightDataSource]}
        targetKeys={targetKeys}
        onChange={onChange}
        leftColumns={columns}
        rightColumns={columns}
        locale={{
          itemUnit: t('adminConsole.memberTransferModal.itemUnit'),
          itemsUnit: t('adminConsole.memberTransferModal.itemsUnit'),
          notFoundContent: t('adminConsole.memberTransferModal.noData'),
          searchPlaceholder: t('adminConsole.memberTransferModal.searchPlaceholder'),
        }}
      />
    </Modal>
  );
};

export default App;
